import {
  AlbumAddIcon,
  AudioIcon,
  CalendarIcon,
  CheckIcon,
  CrossIcon,
  FileIcon,
  FileTextIcon,
  ImageIcon,
  RestartIcon,
  VideoIcon,
} from 'assets/icons';
import { useClickOutside } from 'hooks/click-outside';
import { DateSource, formatDay, formatDayRange } from 'helpers/import-dates';
import { ImportableFile } from 'helpers/import-types';
import { localDateOf } from 'helpers/media-date';
import { classnames } from 'helpers/utils';
import {
  KeyboardEvent,
  ReactElement,
  RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { DayRange, Destination, ExistingAlbum } from '../../review-model';
import styles from './review.module.scss';

const today = () => localDateOf(new Date());

// ---------------------------------------------------------------------------
// Checkbox
// ---------------------------------------------------------------------------

export const Checkbox = ({
  checked,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  indeterminate?: boolean;
  // Omitted for a box that only shows a state (e.g. disabled).
  onChange?: () => void;
  label: string;
  disabled?: boolean;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <span className={styles.check}>
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        readOnly={!onChange}
        disabled={disabled}
        aria-label={label}
      />
      <span aria-hidden>
        {indeterminate ? <i className={styles.dash} /> : <CheckIcon />}
      </span>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Thumbnails
// ---------------------------------------------------------------------------

type FileKind = 'image' | 'video' | 'audio' | 'pdf' | 'other';

export const fileKind = (mimeType: string): FileKind => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  return 'other';
};

export const KIND_LABEL: Record<FileKind, string> = {
  image: 'Photo',
  video: 'Video',
  audio: 'Audio',
  pdf: 'PDF',
  other: 'File',
};

const KIND_ICON: Record<FileKind, ReactElement> = {
  image: <ImageIcon />,
  video: <VideoIcon />,
  audio: <AudioIcon />,
  pdf: <FileTextIcon />,
  other: <FileIcon />,
};

// Only start decoding a preview once the tile is (nearly) on screen.
const useSeen = (ref: RefObject<HTMLElement | null>) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (seen || !element) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [seen, ref]);
  return seen;
};

/**
 * A preview of a file from this computer (photo, or a video's opening frame),
 * falling back to its type icon for other sources and for formats the browser
 * can't render (e.g. HEIC outside Safari).
 */
export const FileThumb = ({
  file,
  className = '',
  iconClassName = '',
}: {
  file: ImportableFile;
  className?: string;
  iconClassName?: string;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const kind = fileKind(file.mimeType);
  const previewable =
    !!file.localFile && (kind === 'image' || kind === 'video');
  const seen = useSeen(ref);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!previewable || !seen || !file.localFile) return undefined;
    const objectUrl = URL.createObjectURL(file.localFile);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [previewable, seen, file.localFile]);

  const showMedia = !!url && !failed;
  return (
    <span ref={ref} className={classnames(styles.thumb, className)}>
      {showMedia && kind === 'image' && (
        <img
          src={url!}
          alt=""
          className={styles.thumbMedia}
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
      {showMedia && kind === 'video' && (
        <video
          src={`${url}#t=0.5`}
          className={styles.thumbMedia}
          preload="metadata"
          muted
          playsInline
          onError={() => setFailed(true)}
        />
      )}
      {!showMedia && (
        <span className={classnames(styles.thumbIcon, iconClassName)}>
          {KIND_ICON[kind]}
        </span>
      )}
      {kind === 'video' && <span className={styles.playBadge} aria-hidden />}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const SOURCE_TEXT: Record<DateSource | 'edited', string> = {
  camera: 'Date taken',
  file: 'File date · estimate',
  none: 'No date found',
  edited: 'Changed by you',
};

// A native date field that commits on Enter / leaving the field and cancels
// on Escape, so half-typed years never reach the plan.
const DayInput = ({
  initial,
  label,
  onCommit,
  onClose,
}: {
  initial: string;
  label: string;
  onCommit: (day: string) => void;
  onClose: () => void;
}) => {
  const [value, setValue] = useState(initial);
  const cancelled = useRef(false);
  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      cancelled.current = true;
      e.currentTarget.blur();
    }
  };
  return (
    <input
      type="date"
      className={styles.dateInput}
      aria-label={label}
      value={value}
      max={today()}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKey}
      onBlur={() => {
        if (!cancelled.current && value && value !== initial) onCommit(value);
        onClose();
      }}
    />
  );
};

/** A memory's date: shows where it came from, click to change it. */
export const DateCell = ({
  date,
  source,
  edited,
  fileName,
  disabled = false,
  onChange,
}: {
  date: string | null;
  source: DateSource;
  edited: boolean;
  fileName: string;
  disabled?: boolean;
  onChange: (day: string | null) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const sourceKey = edited ? 'edited' : source;
  return (
    <div className={styles.dateCell}>
      <span className={styles.chipRow}>
        {editing ? (
          <DayInput
            initial={date ?? ''}
            label={`Date for ${fileName}`}
            onCommit={onChange}
            onClose={() => setEditing(false)}
          />
        ) : (
          <button
            type="button"
            className={classnames(styles.chip, {
              [styles.chipEdited]: edited,
              [styles.chipEmpty]: !date,
            })}
            onClick={() => setEditing(true)}
            disabled={disabled}
            title="Change date"
            aria-label={`${date ? formatDay(date) : 'No date'}. Change the date for ${fileName}`}
          >
            <CalendarIcon />
            {date ? formatDay(date) : 'Add date'}
          </button>
        )}
        {edited && !editing && (
          <button
            type="button"
            className={styles.resetButton}
            onClick={() => onChange(null)}
            title="Use the original date"
            aria-label={`Use the original date for ${fileName}`}
          >
            <RestartIcon />
          </button>
        )}
      </span>
      <span
        className={classnames(styles.source, {
          [styles.sourceFile]: sourceKey === 'file',
          [styles.sourceEdited]: sourceKey === 'edited',
        })}
      >
        {SOURCE_TEXT[sourceKey]}
      </span>
    </div>
  );
};

/** "Set one date for all": applies a single day to every selected memory. */
export const SetAllDates = ({
  onApply,
  disabled = false,
  label = 'Set one date for all',
  className,
}: {
  onApply: (day: string) => void;
  disabled?: boolean;
  label?: string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  if (open) {
    return (
      <DayInput
        initial=""
        label="Date for every selected memory"
        onCommit={onApply}
        onClose={() => setOpen(false)}
      />
    );
  }
  return (
    <button
      type="button"
      className={className ?? styles.ghostButton}
      onClick={() => setOpen(true)}
      disabled={disabled}
    >
      <CalendarIcon />
      {label}
    </button>
  );
};

/** The album's date range: follows its memories until the user sets one. */
export const RangeChip = ({
  range,
  auto,
  overridden,
  albumName,
  disabled = false,
  onChange,
}: {
  range: DayRange | null;
  auto: DayRange | null;
  overridden: boolean;
  albumName: string;
  disabled?: boolean;
  onChange: (range: DayRange | null) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const fieldId = useId();
  useClickOutside(wrapRef, () => setOpen(false));

  const openEditor = () => {
    setStart(range?.start ?? '');
    setEnd(range?.end ?? '');
    setOpen(true);
  };
  const backwards = !!start && !!end && end < start;
  const invalid = !start || !end || backwards;
  const save = () => {
    if (invalid) return;
    const matchesAuto = !!auto && start === auto.start && end === auto.end;
    onChange(matchesAuto ? null : { start, end });
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className={styles.rangeWrap}>
      <button
        type="button"
        className={classnames(styles.chip, {
          [styles.chipEdited]: overridden,
          [styles.chipEmpty]: !range,
        })}
        onClick={() => (open ? setOpen(false) : openEditor())}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Dates for ${albumName}: ${
          range ? formatDayRange(range.start, range.end) : 'none'
        }. Change`}
      >
        <CalendarIcon />
        {range ? formatDayRange(range.start, range.end) : 'Add dates'}
        {range && (
          <span className={styles.chipTag}>
            {overridden ? 'Custom' : 'Auto'}
          </span>
        )}
      </button>
      {open && (
        <div className={styles.popover} role="dialog" aria-label="Album dates">
          <span className={styles.popoverTitle}>Album dates</span>
          <p className={styles.popoverText}>
            {auto
              ? `Set from its memories: ${formatDayRange(auto.start, auto.end)}. Change it if the album covers a different period.`
              : 'None of its memories has a date yet. Set the period the album covers.'}
          </p>
          <div className={styles.fieldRow}>
            <label className={styles.field} htmlFor={`${fieldId}-from`}>
              From
              <input
                id={`${fieldId}-from`}
                type="date"
                value={start}
                max={end || today()}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label className={styles.field} htmlFor={`${fieldId}-to`}>
              To
              <input
                id={`${fieldId}-to`}
                type="date"
                value={end}
                min={start || undefined}
                max={today()}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
          </div>
          {backwards && (
            <span className={styles.fieldError}>
              The end date is before the start date.
            </span>
          )}
          <div className={styles.popoverActions}>
            <button
              type="button"
              className={styles.linkButton}
              disabled={!overridden}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              Match memories
            </button>
            <button
              type="button"
              className={styles.primarySmall}
              disabled={invalid}
              onClick={save}
            >
              Save dates
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Destination picker for unsorted files
// ---------------------------------------------------------------------------

const encode = (destination: Destination) => {
  if (destination.kind === 'album') return `a|${destination.albumKey}`;
  if (destination.kind === 'existing') return `e|${destination.albumId}`;
  return 'u';
};

const decode = (value: string): Destination => {
  if (value.startsWith('a|'))
    return { kind: 'album', albumKey: value.slice(2) };
  if (value.startsWith('e|')) {
    return { kind: 'existing', albumId: Number(value.slice(2)) };
  }
  return { kind: 'unsorted' };
};

export type DestinationOptions = {
  importAlbums: { key: string; name: string }[];
  existingAlbums: ExistingAlbum[];
};

type DestinationSelectProps = DestinationOptions & {
  value: Destination | null;
  onChange: (destination: Destination) => void;
  label: string;
  mixed?: boolean;
  // Shown as a pill button with this text; the list opens from it.
  pillLabel?: string;
};

const NEW_ALBUM = 'new';
const ALBUM_NAME_MAX = 100;

const DestinationSelect = ({
  value,
  onChange,
  onNew,
  selectRef,
  importAlbums,
  existingAlbums,
  label,
  mixed = false,
  pillLabel,
}: DestinationSelectProps & {
  onNew: () => void;
  selectRef: RefObject<HTMLSelectElement | null>;
}) => {
  const select = (
    <select
      ref={selectRef}
      className={
        pillLabel
          ? styles.pillSelectInput
          : classnames(styles.select, {
              [styles.selectAssigned]: !!value && value.kind !== 'unsorted',
            })
      }
      value={mixed || !value ? '' : encode(value)}
      onChange={(e) => {
        const next = e.target.value;
        if (next === NEW_ALBUM) onNew();
        else if (next) onChange(decode(next));
      }}
      aria-label={label}
    >
      {mixed && (
        <option value="" disabled>
          Mixed
        </option>
      )}
      <option value="u">Leave unsorted</option>
      <option value={NEW_ALBUM}>+ New album…</option>
      {importAlbums.length > 0 && (
        <optgroup label="Albums in this import">
          {importAlbums.map((album) => (
            <option key={album.key} value={`a|${album.key}`}>
              {album.name}
            </option>
          ))}
        </optgroup>
      )}
      {existingAlbums.length > 0 && (
        <optgroup label="Already in this vault">
          {existingAlbums.map((album) => (
            <option key={album.id} value={`e|${album.id}`}>
              {album.name}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
  if (!pillLabel) return select;
  // The native list, invisible, over a pill that says what it does.
  return (
    <span className={styles.pillSelect}>
      <AlbumAddIcon />
      <span className={styles.pillSelectLabel}>{pillLabel}</span>
      {select}
    </span>
  );
};

// Names the album picked from "+ New album…". Enter or ✓ creates it, Escape
// or ✕ backs out; clicking away only backs out while the name is empty.
const NewAlbumField = ({
  onCreate,
  onCancel,
}: {
  onCreate: (name: string) => void;
  onCancel: (refocus: boolean) => void;
}) => {
  const [name, setName] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const trimmed = name.trim();
  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && trimmed) onCreate(trimmed);
    if (e.key === 'Escape') onCancel(true);
  };
  return (
    <div
      ref={wrapRef}
      className={styles.newAlbum}
      onBlur={(e) => {
        if (trimmed) return;
        if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) {
          onCancel(false);
        }
      }}
    >
      <input
        className={styles.newAlbumInput}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKey}
        placeholder="New album name"
        maxLength={ALBUM_NAME_MAX}
        aria-label="Name for the new album"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      <button
        type="button"
        className={classnames(styles.newAlbumButton, styles.newAlbumSave)}
        disabled={!trimmed}
        onClick={() => onCreate(trimmed)}
        title="Create album"
        aria-label="Create album"
      >
        <CheckIcon />
      </button>
      <button
        type="button"
        className={styles.newAlbumButton}
        onClick={() => onCancel(true)}
        title="Cancel"
        aria-label="Cancel new album"
      >
        <CrossIcon />
      </button>
    </div>
  );
};

/**
 * Where an unsorted file goes: leave it unsorted, an album in this import, an
 * album already in the vault, or a new album named right here.
 */
export const DestinationPicker = ({
  onCreateAlbum,
  ...props
}: DestinationSelectProps & { onCreateAlbum: (name: string) => void }) => {
  const [naming, setNaming] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (naming || !refocus.current) return;
    refocus.current = false;
    selectRef.current?.focus();
  }, [naming]);

  const close = (focusSelect: boolean) => {
    refocus.current = focusSelect;
    setNaming(false);
  };

  if (naming) {
    return (
      <NewAlbumField
        onCreate={(name) => {
          onCreateAlbum(name);
          close(true);
        }}
        onCancel={close}
      />
    );
  }
  return (
    <DestinationSelect
      {...props}
      selectRef={selectRef}
      onNew={() => setNaming(true)}
    />
  );
};
